import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Upload, CheckCircle2, Loader2, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DOC_LABELS = {
  diplome: "Diplôme (le plus élevé)",
  cni: "Carte d'identité ou passeport",
  cv: "Curriculum Vitae",
  lettre_motivation: "Lettre de motivation",
  photo1: "Photo d'identité 1",
  photo2: "Photo d'identité 2",
};

export default function Recrutement() {
  const [step, setStep] = useState(1); // 1=infos, 2=upload docs, 3=confirmation
  const [applicationId, setApplicationId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null); // doc key currently uploading
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [form, setForm] = useState({
    first_name: "", last_name: "", gender: "M", birth_date: "",
    email: "", phone: "", address: "",
    subjects_taught: "", experience_years: 0, highest_degree: "",
    cover_letter_text: "",
  });

  const submitInfo = async (e) => {
    e.preventDefault();
    const required = ["first_name","last_name","birth_date","email","phone"];
    for (const k of required) {
      if (!form[k]) return toast.error(`Le champ ${k.replace('_',' ')} est requis`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/recruitment/candidates/public", {
        ...form,
        experience_years: Number(form.experience_years) || 0,
      });
      setApplicationId(data.application_id);
      setStep(2);
      toast.success("Informations enregistrées. Téléversez maintenant vos documents.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 8 Mo)");
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append("doc_type", docType);
      formData.append("file", file);
      const { data } = await api.post(`/recruitment/candidates/${applicationId}/upload-doc`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocs({ ...uploadedDocs, [docType]: { path: data.path, filename: file.name } });
      toast.success(`"${DOC_LABELS[docType]}" téléversé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(null);
    }
  };

  const allDocsUploaded = Object.keys(DOC_LABELS).every(k => uploadedDocs[k]);
  const finalize = () => setStep(3);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-20">
        <div className="text-center mb-8">
          <div className="chip mb-3"><Briefcase className="h-3 w-3" /><span>REJOIGNEZ-NOUS</span></div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#1E3A8A] mb-3">Devenez enseignant aux Écoles MAZID</h1>
          <p className="text-slate-600 max-w-xl mx-auto">Postulez en ligne : renseignez vos informations, téléversez vos documents. Notre direction étudiera votre dossier sous 5 à 10 jours ouvrés.</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="steps">
          {[1,2,3].map(n => (
            <div key={n} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= n ? "bg-[#1E3A8A] text-white" : "bg-slate-200 text-slate-500"}`}>{n}</div>
              {n < 3 && <div className={`w-16 h-1 ${step > n ? "bg-[#1E3A8A]" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={submitInfo} className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 space-y-4" data-testid="step1-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Prénom *</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="rec-firstname" required /></div>
              <div><Label>Nom *</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="rec-lastname" required /></div>
              <div>
                <Label>Sexe</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({...form, gender: v})}>
                  <SelectTrigger data-testid="rec-gender"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Homme</SelectItem><SelectItem value="F">Femme</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date de naissance *</Label><Input type="date" value={form.birth_date} onChange={e => setForm({...form, birth_date: e.target.value})} data-testid="rec-birthdate" required /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="rec-email" required /></div>
              <div><Label>Téléphone *</Label><Input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+224 622 00 00 00" data-testid="rec-phone" required /></div>
              <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Quartier, Commune, Conakry" data-testid="rec-address" /></div>
              <div><Label>Matières enseignées</Label><Input value={form.subjects_taught} onChange={e => setForm({...form, subjects_taught: e.target.value})} placeholder="Mathématiques, Physique-Chimie" data-testid="rec-subjects" /></div>
              <div><Label>Années d&apos;expérience</Label><Input type="number" min="0" value={form.experience_years} onChange={e => setForm({...form, experience_years: e.target.value})} data-testid="rec-experience" /></div>
              <div className="sm:col-span-2"><Label>Plus haut diplôme obtenu</Label><Input value={form.highest_degree} onChange={e => setForm({...form, highest_degree: e.target.value})} placeholder="Master 2 en Mathématiques" data-testid="rec-degree" /></div>
              <div className="sm:col-span-2"><Label>Message de motivation (optionnel)</Label><Textarea rows={4} value={form.cover_letter_text} onChange={e => setForm({...form, cover_letter_text: e.target.value})} placeholder="Présentez-vous en quelques lignes..." data-testid="rec-motivation" /></div>
            </div>
            <div className="pt-4 border-t border-slate-200">
              <Button type="submit" disabled={submitting} className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white h-12" data-testid="rec-submit-step1">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <>Continuer <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 space-y-3" data-testid="step2-uploads">
            <h2 className="font-heading font-bold text-xl text-[#1E3A8A]">Téléversez vos documents</h2>
            <p className="text-sm text-slate-500 mb-4">Formats acceptés : PDF, JPG, PNG (max 8 Mo par fichier).</p>
            {Object.entries(DOC_LABELS).map(([key, label]) => (
              <div key={key} className={`border rounded-lg p-4 flex items-center justify-between ${uploadedDocs[key] ? "border-green-300 bg-green-50" : "border-slate-200"}`}>
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{label} *</div>
                  {uploadedDocs[key] && <div className="text-xs text-green-700 mt-1"><CheckCircle2 className="inline h-3 w-3 mr-1" />{uploadedDocs[key].filename}</div>}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => uploadDoc(key, e.target.files?.[0])} data-testid={`upload-${key}`} />
                  <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${uploadedDocs[key] ? "bg-green-600 hover:bg-green-700 text-white" : "bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white"} flex items-center gap-2`}>
                    {uploading === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploadedDocs[key] ? "Remplacer" : "Choisir"}
                  </div>
                </label>
              </div>
            ))}
            <Button onClick={finalize} disabled={!allDocsUploaded} className="w-full bg-[#166534] hover:bg-[#14532d] text-white h-12 mt-4" data-testid="rec-submit-step2">
              <CheckCircle2 className="h-5 w-5 mr-2" />Soumettre ma candidature
            </Button>
            {!allDocsUploaded && <p className="text-xs text-orange-600 text-center">Veuillez téléverser tous les documents requis avant de soumettre.</p>}
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 text-center" data-testid="step3-confirmation">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="font-heading font-extrabold text-2xl text-[#1E3A8A] mb-3">Candidature soumise !</h2>
            <p className="text-slate-600 mb-4">Merci {form.first_name} pour votre candidature aux Écoles MAZID.</p>
            <div className="bg-slate-50 rounded-lg p-4 mb-4 text-left inline-block">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Votre référence de dossier</div>
              <div className="font-mono text-sm text-[#1E3A8A] font-bold">{applicationId}</div>
              <div className="text-xs text-slate-500 mt-2">Conservez cette référence pour suivre votre candidature.</div>
            </div>
            <p className="text-sm text-slate-500">Notre direction examinera votre dossier sous 5 à 10 jours ouvrés. Vous serez notifié(e) par email à <strong>{form.email}</strong>.</p>
            <Button onClick={() => window.location.href = "/"} variant="outline" className="mt-6">Retour à l&apos;accueil</Button>
          </div>
        )}
      </div>
    </div>
  );
}
